<section class="schedules" aria-labelledby="recurring-schedules-title">
  <header class="schedule-header">
    <div>
      <h3 id="recurring-schedules-title">
        {{ workflowCode ? "Automation / Entry points" : "Recurring schedules" }}
      </h3>
      <p>
        {{
          workflowCode
            ? "Schedules for this workflow. Schedule versions are published separately from the workflow draft."
            : "Versioned local-date schedules and their captured work."
        }}
      </p>
    </div>
    <button
      uiButton
      type="button"
      (click)="refresh()"
      [disabled]="busy || interactionBlocked || !canRead()"
    >
      Refresh schedules
    </button>
  </header>
  <p *ngIf="!canRead() || accessDenied" class="schedule-error" role="alert">
    You do not currently have access to recurring schedules. Entered settings
    and reasons are kept locally.
  </p>
  <p *ngIf="loading" role="status">Loading schedules…</p>
  <section *ngIf="error as failure" class="schedule-error" role="alert">
    <strong>{{ failure.title }}</strong>
    <p *ngFor="let message of failure.messages">{{ message }}</p>
    <p>
      Entered settings and reasons are kept. Refresh the source before retrying
      a stale operation.
    </p>
  </section>
  <p *ngIf="validation" class="schedule-error" role="alert">{{ validation }}</p>
  <p *ngIf="notice" class="schedule-notice" role="status">{{ notice }}</p>
  <ng-container *ngIf="canRead() && !accessDenied && catalog as data">
    <p class="schedule-notice">
      Schedule producer:
      <strong>{{
        data.producerEnabled
          ? "Enabled by installation"
          : "Disabled by installation"
      }}</strong
      >. Schedule controls cannot override this setting. Event outbox dispatch
      has its own worker control above.
    </p>
    <p *ngIf="!data.targets.length" class="schedule-notice">
      No compatible scheduled operation is installed. You can create and save an
      incomplete draft; publication requires an installed target.
    </p>
    <form
      *ngIf="canAuthor() && (!workflowCode || data.targets.length)"
      class="schedule-actions"
      (ngSubmit)="create()"
    >
      <label
        >New schedule name<input
          uiControl
          name="newScheduleLabel"
          maxlength="160"
          [(ngModel)]="newLabel"
          (ngModelChange)="changed()"
          [disabled]="!writable || loading" /></label
      ><button
        uiButton
        type="submit"
        [disabled]="!writable || loading || draftDirty || !newLabel.trim()"
      >
        Create draft
      </button>
    </form>
    <p *ngIf="!versions.length && !loading">
      No recurring schedules have been created.
    </p>
    <ui-grid
      *ngIf="versions.length"
      [rowData]="versions"
      [columnDefs]="versionColumns"
      [agGridOptions]="versionGridOptions"
      [showPanelHeader]="false"
      [showStandardFilterControls]="false"
      [showBlankRowsOnLastPage]="false"
      [autoHidePaginator]="true"
      containerClass="no-panel"
      width="fill"
    />
    <label *ngIf="versions.length" class="schedule-choice"
      >Open schedule version<select
        uiControl
        [ngModel]="selected?.id || null"
        [ngModelOptions]="{ standalone: true }"
        (ngModelChange)="selectVersionById($event)"
        [disabled]="busy || interactionBlocked"
      >
        <option [ngValue]="null">Choose a version</option>
        <option *ngFor="let row of versions" [ngValue]="row.id">
          {{ row.definition.label }} · v{{ row.definition.version }} ·
          {{ row.statusCode }}
        </option>
      </select></label
    >
    <p *ngIf="data.historyTruncated" class="schedule-muted">
      The catalog is bounded. Older version records are retained by the server.
    </p>
    <ng-container *ngIf="definition as form">
      <header class="schedule-header">
        <div>
          <h4>{{ form.label }} · version {{ form.version }}</h4>
          <p>
            {{
              selected?.statusCode === "published"
                ? "Published version · immutable"
                : "Draft settings"
            }}
            · {{ draftDirty ? "Unsaved changes" : "Saved settings" }}
          </p>
        </div>
        <div class="schedule-actions">
          <button
            *ngIf="canAuthor() && selected?.statusCode === 'published'"
            uiButton
            type="button"
            (click)="create(true)"
            [disabled]="!writable || loading"
          >
            Create next draft / resume draft</button
          ><button
            uiButton
            type="button"
            (click)="reloadSelected()"
            [disabled]="busy || interactionBlocked"
          >
            Reload saved settings
          </button>
        </div>
      </header>
      <p *ngIf="staleDraft" class="schedule-notice" role="status">
        This draft source changed or is unavailable. Your local settings are
        kept; refresh and explicitly reload saved settings to resolve it.
      </p>
      <section
        *ngIf="control as currentControl"
        aria-label="Run published entry point now"
        class="schedule-run"
      >
        <h4>Run now</h4>
        <p *ngIf="selectedPublishedVersion as version">
          Selected execution: {{ version.definition.label }} · published v{{
            version.definition.version
          }}
          · {{ version.definition.localTime }}
          {{ version.definition.timeZoneId }}
        </p>
        <p>
          Runs published settings using the current date in the published time
          zone. Future scheduled starts stay unchanged. Unsaved workflow changes
          are not executed.
        </p>
        <p *ngIf="!canRunNow" class="schedule-muted">
          Requires a selected published schedule, enabled installation, saved
          schedule controls and no unfinished capture. The server also checks
          unfinished enrollment work and current worker permissions.
        </p>
        <label
          >Run reason<textarea
            uiControl
            name="runNowReason"
            maxlength="1000"
            [(ngModel)]="runNowReason"
            (ngModelChange)="changed()"
            [disabled]="!writable"
          ></textarea>
        </label>
        <button
          uiButton
          type="button"
          (click)="runNow()"
          [disabled]="!canRunNow || !runNowReason.trim()"
        >
          Run published settings now
        </button>
        <p *ngIf="currentControl.nextLocalDate">
          Next scheduled date checkpoint: {{ currentControl.nextLocalDate
          }}{{ currentControl.enabled ? "" : " · Future starts paused" }}
        </p>
      </section>
      <details
        *ngIf="target as operation"
        class="schedule-notice"
        aria-label="Entry point configuration"
      >
        <summary>Entry point scope, source and destination</summary>
        <dl>
          <dt>Execution identity</dt>
          <dd>
            Service {{ operation.registration.serviceExternalIdentityId }} ·
            {{ operation.registration.serviceTenantId }}
          </dd>
          <dt>Scope</dt>
          <dd>
            {{ operation.registration.dataScope.scopeTypeCode }} /
            {{ operation.registration.dataScope.scopeKey }}
          </dd>
          <ng-container *ngIf="operation.entryPoint as entry">
            <dt>Destination</dt>
            <dd>
              {{ entry.workflowCode }} →
              {{ entry.preparationStepCode }} (unassigned)
            </dd>
            <dt>Candidate selector</dt>
            <dd>{{ entry.candidateSelector }}</dd>
            <dt>Enrollment</dt>
            <dd>{{ entry.enrollmentBehavior }}</dd>
            <dt>Entry worker</dt>
            <dd>
              {{
                entry.workerEnabled
                  ? "Enabled by installation"
                  : "Disabled by installation"
              }}
            </dd>
          </ng-container>
        </dl>
      </details>
      <form class="schedule-editor" (ngSubmit)="save()">
        <div class="schedule-fields">
          <label
            >Schedule name<input
              uiControl
              name="scheduleLabel"
              maxlength="160"
              [(ngModel)]="form.label"
              (ngModelChange)="changed()"
              [disabled]="!editable"
          /></label>
          <label
            >Registered operation<select
              uiControl
              name="target"
              [ngModel]="targetKey"
              (ngModelChange)="selectTarget($event)"
              [disabled]="!editable"
            >
              <option *ngIf="!target" [value]="targetKey">
                {{
                  form.triggerCode
                    ? "Unavailable retained operation: " +
                      form.triggerCode +
                      " v" +
                      form.triggerVersion
                    : "Choose an installed operation"
                }}
              </option>
              <option
                *ngFor="let operation of data.targets"
                [value]="targetIdentity(operation)"
              >
                {{ operation.label }} · v{{ operation.version }}
              </option>
            </select></label
          >
          <label
            >Exact calendar version<select
              uiControl
              name="calendar"
              [ngModel]="calendarKey"
              (ngModelChange)="selectCalendar($event)"
              [disabled]="!editable"
            >
              <option *ngIf="!calendar" [value]="calendarKey">
                {{
                  form.calendarCode
                    ? "Unavailable retained calendar: " +
                      form.calendarCode +
                      " / " +
                      form.calendarVersionCode
                    : "Choose a calendar"
                }}
              </option>
              <option
                *ngFor="let source of data.calendars"
                [value]="calendarIdentity(source)"
              >
                {{ source.label }} · {{ source.versionCode }} ·
                {{ source.timeZoneId }}
              </option>
            </select></label
          >
          <label
            >Time zone<input
              uiControl
              name="timeZone"
              [ngModel]="form.timeZoneId"
              disabled
            /><span class="schedule-muted"
              >Fixed to the selected calendar.</span
            ></label
          >
          <label
            >Start date<input
              uiControl
              name="startDate"
              type="date"
              [(ngModel)]="form.startDate"
              (ngModelChange)="changed()"
              [disabled]="!editable"
          /></label>
          <label
            >End date (exclusive)<input
              uiControl
              name="endDate"
              type="date"
              [ngModel]="form.endDate || ''"
              (ngModelChange)="form.endDate = $event || null; changed()"
              [disabled]="!editable"
            /><span class="schedule-muted"
              >Required for publication; active window is at most ten
              years.</span
            ></label
          >
          <label
            >Local start time<input
              uiControl
              name="localTime"
              type="time"
              step="60"
              [(ngModel)]="form.localTime"
              (ngModelChange)="changed()"
              [disabled]="!editable"
          /></label>
          <label
            >Repeat every (local days)<input
              uiControl
              name="intervalDays"
              type="number"
              min="1"
              max="366"
              step="1"
              [(ngModel)]="form.intervalDays"
              (ngModelChange)="changed()"
              [disabled]="!editable"
          /></label>
          <label class="schedule-check"
            ><input
              name="workingDaysOnly"
              type="checkbox"
              [(ngModel)]="form.workingDaysOnly"
              (ngModelChange)="changed()"
              [disabled]="!editable"
            />
            Only the calendar's working days</label
          >
          <label
            >DST gap<select
              uiControl
              name="gapPolicy"
              [(ngModel)]="form.gapPolicy"
              (ngModelChange)="changed()"
              [disabled]="!editable"
            >
              <option
                *ngIf="form.gapPolicy !== 'skip'"
                [value]="form.gapPolicy"
              >
                Unsupported: {{ form.gapPolicy }}
              </option>
              <option value="skip">Skip nonexistent local time</option>
            </select></label
          >
          <label
            >Repeated local time<select
              uiControl
              name="ambiguousTimePolicy"
              [(ngModel)]="form.ambiguousTimePolicy"
              (ngModelChange)="changed()"
              [disabled]="!editable"
            >
              <option value="earlier">Earlier instant</option>
              <option value="later">Later instant</option>
              <option
                *ngIf="
                  form.ambiguousTimePolicy !== 'earlier' &&
                  form.ambiguousTimePolicy !== 'later'
                "
                [value]="form.ambiguousTimePolicy"
              >
                Unsupported: {{ form.ambiguousTimePolicy }}
              </option>
            </select></label
          >
          <label
            >Missed starts<select
              uiControl
              name="misfirePolicy"
              [(ngModel)]="form.misfirePolicy"
              (ngModelChange)="changed()"
              [disabled]="!editable"
            >
              <option value="skip">Skip missed occurrences</option>
              <option value="catch-up">Bounded catch-up</option>
              <option
                *ngIf="
                  form.misfirePolicy !== 'skip' &&
                  form.misfirePolicy !== 'catch-up'
                "
                [value]="form.misfirePolicy"
              >
                Unsupported: {{ form.misfirePolicy }}
              </option>
            </select></label
          >
          <label
            >Maximum catch-up per cycle<input
              uiControl
              name="catchUpLimit"
              type="number"
              min="1"
              max="32"
              step="1"
              [(ngModel)]="form.catchUpLimit"
              (ngModelChange)="changed()"
              [disabled]="!editable"
          /></label>
          <label
            >Overlapping work<select
              uiControl
              name="overlapPolicy"
              [(ngModel)]="form.overlapPolicy"
              (ngModelChange)="changed()"
              [disabled]="!editable"
            >
              <option value="skip-while-unfinished">
                Skip while prior work is unfinished
              </option>
              <option value="allow">Allow overlapping occurrences</option>
              <option
                *ngIf="
                  form.overlapPolicy !== 'allow' &&
                  form.overlapPolicy !== 'skip-while-unfinished'
                "
                [value]="form.overlapPolicy"
              >
                Unsupported: {{ form.overlapPolicy }}
              </option>
            </select></label
          >
        </div>
        <details open>
          <summary>Operation parameters</summary>
          <phms-schedule-parameters
            *ngIf="target as operation"
            [fields]="operation.registration.payloadSchema.fields"
            [values]="parameters"
            [disabled]="!editable"
            (valuesChange)="setParameters($event)"
          />
          <p *ngIf="!target">
            The saved parameter values are preserved until the exact operation
            is available or explicitly replaced.
          </p>
          <pre *ngIf="!target">{{ form.parameters | json }}</pre>
        </details>
        <details>
          <summary>Exact source and execution policy</summary>
          <dl class="schedule-facts">
            <div>
              <dt>Schedule identity</dt>
              <dd>
                {{ form.code }} · v{{ form.version }} · revision
                {{ selected?.revision }}
              </dd>
            </div>
            <div>
              <dt>Operation reference</dt>
              <dd>
                {{ form.triggerCode }} · v{{ form.triggerVersion }} ·
                {{ form.triggerHash }}
              </dd>
            </div>
            <div>
              <dt>Calendar reference</dt>
              <dd>
                {{ form.calendarCode }} / {{ form.calendarVersionCode }} ·
                {{ form.calendarContentHash }}
              </dd>
            </div>
            <div>
              <dt>Workflow selection</dt>
              <dd>{{ form.workflowSelectionPolicy }}</dd>
            </div>
            <div>
              <dt>Configuration selection</dt>
              <dd>{{ form.configurationSelectionPolicy }}</dd>
            </div>
          </dl>
          <ng-container *ngIf="target as operation"
            ><p>
              Scoped system caller:
              {{ operation.registration.serviceTenantId }} · identity
              {{ operation.registration.serviceExternalIdentityId }}. Data scope
              {{ operation.registration.dataScope.scopeTypeCode }} /
              {{ operation.registration.dataScope.scopeKey }}; owner scope
              {{ operation.registration.ownerScope.scopeTypeCode }} /
              {{ operation.registration.ownerScope.scopeKey }}.
            </p>
            <p>
              Module {{ operation.registration.moduleCode }} v{{
                operation.registration.moduleVersion
              }}
              · command {{ operation.registration.command.key.code }} v{{
                operation.registration.command.key.version
              }}.
            </p></ng-container
          >
          <p class="schedule-muted">
            Registered command eligibility and authority are rechecked at
            capture. Published parameters and prior run snapshots remain exact;
            a new version does not rewrite them. Misfire grace is five minutes.
          </p>
        </details>
        <div *ngIf="selected?.statusCode === 'draft'" class="schedule-actions">
          <button uiButton type="submit" [disabled]="!editable || !draftDirty">
            Save draft</button
          ><button
            *ngIf="canPublish()"
            uiButton
            type="button"
            (click)="publish()"
            [disabled]="!canPublishVersion"
          >
            Publish version
          </button>
        </div>
        <details
          *ngIf="selected?.statusCode === 'draft' && publicationErrors.length"
        >
          <summary>
            Publication needs {{ publicationErrors.length }} correction{{
              publicationErrors.length === 1 ? "" : "s"
            }}
          </summary>
          <ul>
            <li *ngFor="let issue of publicationErrors">{{ issue }}</li>
          </ul>
        </details>
      </form>
      <section
        *ngIf="control as source"
        class="schedule-control"
        aria-labelledby="schedule-control-title"
      >
        <h4 id="schedule-control-title">Scheduling control</h4>
        <p>
          Current selection:
          {{
            source.publishedVersionId
              ? "published version record " + source.publishedVersionId
              : "No version selected"
          }}
          · {{ source.enabled ? "Enabled" : "Disabled" }} · next local date
          {{ source.nextLocalDate || "not selected" }} · revision
          {{ source.revision }}.
        </p>
        <p *ngIf="source.lastReason">Last reason: {{ source.lastReason }}</p>
        <p *ngIf="source.issueCode" class="schedule-notice">
          {{ source.issueCode }}
        </p>
        <p *ngIf="staleControl" class="schedule-notice">
          Control revision changed. Refresh and reload saved settings before
          retrying; your entered reason is kept.
        </p>
        <form
          *ngIf="canAuthor() && (!workflowCode || data.targets.length)"
          (ngSubmit)="changeControl()"
        >
          <div class="schedule-fields">
            <label
              >Published version for future work<select
                uiControl
                name="controlVersion"
                [(ngModel)]="controlVersionId"
                (ngModelChange)="changed()"
                [disabled]="!writable || staleControl || loading"
              >
                <option [ngValue]="null">Choose a published version</option>
                <option
                  *ngFor="let row of publishedVersions"
                  [ngValue]="row.id"
                >
                  {{ row.definition.label }} · v{{ row.definition.version }}
                </option>
              </select></label
            ><label class="schedule-check"
              ><input
                type="checkbox"
                name="controlEnabled"
                [(ngModel)]="controlEnabled"
                (ngModelChange)="changed()"
                [disabled]="!writable || staleControl || loading"
              />
              Enable future starts</label
            ><label
              >Existing scheduled work<select
                uiControl
                name="existingWorkPolicy"
                [(ngModel)]="existingWorkPolicy"
                (ngModelChange)="changed()"
                [disabled]="!writable || staleControl || loading"
              >
                <option value="drain">Drain retained work</option>
                <option value="cancel-pending">
                  Cancel work not yet captured
                </option>
              </select></label
            ><label
              >Control reason<textarea
                uiControl
                name="controlReason"
                rows="2"
                maxlength="1000"
                [(ngModel)]="controlReason"
                (ngModelChange)="changed()"
                [disabled]="!writable || loading"
              ></textarea>
            </label>
          </div>
          <button
            uiButton
            type="submit"
            [disabled]="
              !writable ||
              staleControl ||
              loading ||
              !controlVersionId ||
              !controlReason.trim()
            "
          >
            Apply scheduling control
          </button>
        </form>
        <p class="schedule-muted">
          Disabling prevents future starts. Cancel pending fences only work not
          yet captured by the scheduler. Already committed invocations continue
          through their existing outbox lifecycle; this control cannot undo
          their effects.
        </p>
      </section>
    </ng-container>
    <section class="schedule-history" aria-labelledby="schedule-runs-title">
      <header class="schedule-header">
        <h4 id="schedule-runs-title">
          Schedule run history{{
            selected ? " · " + selected.definition.label : ""
          }}
        </h4>
        <button
          uiButton
          type="button"
          (click)="loadRuns()"
          [disabled]="busy || interactionBlocked || loadingRuns"
        >
          Refresh runs
        </button>
      </header>
      <p class="schedule-muted">
        Latest 50 occurrences. Scheduling capture state and downstream delivery
        state are separate.
      </p>
      <p *ngIf="loadingRuns" role="status">Loading run history…</p>
      <section *ngIf="runsError as failure" class="schedule-error" role="alert">
        <strong>{{ failure.title }}</strong>
        <p *ngFor="let message of failure.messages">{{ message }}</p>
      </section>
      <p *ngIf="!loadingRuns && !runsError && !runs.length">
        No schedule runs have been recorded.
      </p>
      <ui-grid
        *ngIf="runs.length"
        [rowData]="runs"
        [columnDefs]="runColumns"
        [agGridOptions]="runGridOptions"
        [showPanelHeader]="false"
        [showStandardFilterControls]="false"
        [showBlankRowsOnLastPage]="false"
        [autoHidePaginator]="true"
        containerClass="no-panel"
        width="fill"
      /><label *ngIf="runs.length" class="schedule-choice"
        >Inspect run<select
          uiControl
          [ngModel]="selectedRun?.id || ''"
          [ngModelOptions]="{ standalone: true }"
          (ngModelChange)="selectRunById($event)"
          [disabled]="busy || interactionBlocked"
        >
          <option value="">Choose a run</option>
          <option *ngFor="let row of runs" [value]="row.id">
            {{ row.localDate }} · v{{ row.definitionVersion }} ·
            {{ row.status }} · {{ row.id }}
          </option>
        </select></label
      >
      <section *ngIf="selectedRun as run" class="schedule-run">
        <h4>
          {{ run.runKind === "manual" ? "Run now" : "Scheduled occurrence" }}
          {{ run.localDate }} · version {{ run.definitionVersion }}
        </h4>
        <dl class="schedule-facts">
          <div>
            <dt>Run identity</dt>
            <dd>{{ run.id }}</dd>
          </div>
          <div>
            <dt>Scheduling state</dt>
            <dd>{{ run.status }}</dd>
          </div>
          <div>
            <dt>Nominal instant (UTC)</dt>
            <dd>{{ run.nominalUtc || "No valid nominal instant" }}</dd>
          </div>
          <div>
            <dt>Due (UTC)</dt>
            <dd>{{ run.dueUtc }}</dd>
          </div>
          <div>
            <dt>Capture attempts / lease</dt>
            <dd>{{ run.attempts }} / {{ run.leaseUntilUtc || "none" }}</dd>
          </div>
          <div>
            <dt>Captured invocation</dt>
            <dd>{{ run.invocationId || "Not captured" }}</dd>
          </div>
          <div>
            <dt>Delivery state</dt>
            <dd>{{ run.deliveryStatus || "No delivery yet" }}</dd>
          </div>
          <div>
            <dt>Prior run</dt>
            <dd>{{ run.rerunOf || "Original occurrence" }}</dd>
          </div>
          <div>
            <dt>Last update (UTC)</dt>
            <dd>{{ run.updatedUtc }}</dd>
          </div>
        </dl>
        <phms-workflow-enrollment-run-inspector
          *ngIf="target?.entryPoint && run.invocationId"
          [invocationId]="run.invocationId!"
        />
        <p *ngIf="run.issueCode" class="schedule-notice">{{ run.issueCode }}</p>
        <p *ngIf="run.reason">Creation reason: {{ run.reason }}</p>
        <p *ngIf="run.lastRecoveryReason">
          Last recovery reason: {{ run.lastRecoveryReason }}
        </p>
        <p *ngIf="staleRun" class="schedule-notice">
          Run state changed. Refresh and select the current run before recovery;
          your reason is kept.
        </p>
        <button
          *ngIf="run.invocationId && run.deliveryConsumerId"
          uiButton
          type="button"
          (click)="openDelivery()"
          [disabled]="busy || interactionBlocked"
        >
          Inspect captured delivery above
        </button>
        <form
          *ngIf="canAuthor() && run.recoveryActions.length"
          (ngSubmit)="$event.preventDefault()"
        >
          <label
            >Recovery reason<textarea
              uiControl
              name="recoveryReason"
              rows="2"
              maxlength="1000"
              [(ngModel)]="recoveryReason"
              (ngModelChange)="changed()"
              [disabled]="!writable"
            ></textarea>
          </label>
          <div class="schedule-actions">
            <button
              *ngIf="run.recoveryActions.includes('retry')"
              uiButton
              type="button"
              (click)="recover('retry')"
              [disabled]="
                !writable || staleRun || loadingRuns || !recoveryReason.trim()
              "
            >
              Retry same occurrence capture</button
            ><button
              *ngIf="run.recoveryActions.includes('rerun')"
              uiButton
              type="button"
              (click)="recover('rerun')"
              [disabled]="
                !writable || staleRun || loadingRuns || !recoveryReason.trim()
              "
            >
              Create distinct rerun
            </button>
          </div>
        </form>
        <p class="schedule-muted">
          Retry retains this occurrence and its snapshot. A deliberate rerun
          creates new work with a reason and current eligibility. Recovery of an
          already captured dead-letter invocation is available through its
          delivery above.
        </p>
      </section>
    </section>
  </ng-container>
</section>
